import { useCallback, useEffect, useRef, useState } from 'react'
import { buildRecordedAudioFilename } from 'utils/chatAttachmentLimits'
import { convertChatAudioForSend } from 'utils/convertChatAudioForSend'

export type ChatAudioRecorderPhase =
  | 'idle'
  | 'recording'
  | 'processing'
  | 'preview'

function pickRecorderMime(): string {
  if (typeof MediaRecorder === 'undefined') return ''
  const candidates = [
    'audio/mp4',
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/ogg;codecs=opus',
    'audio/ogg',
  ]
  for (const c of candidates) {
    if (MediaRecorder.isTypeSupported(c)) return c
  }
  return ''
}

export function formatRecordingSeconds(total: number): string {
  const s = Math.max(0, Math.floor(total))
  const m = Math.floor(s / 60)
  const r = s % 60
  return `${m}:${String(r).padStart(2, '0')}`
}

export function useChatAudioRecorder(options?: { maxSeconds?: number }) {
  const maxSeconds = options?.maxSeconds ?? 120
  const [phase, setPhase] = useState<ChatAudioRecorderPhase>('idle')
  const [seconds, setSeconds] = useState(0)
  const [recordedDurationSeconds, setRecordedDurationSeconds] = useState(0)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [recordedFile, setRecordedFile] = useState<File | null>(null)
  const [error, setError] = useState<string | null>(null)

  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const chunksRef = useRef<Blob[]>([])
  const streamRef = useRef<MediaStream | null>(null)
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const startedAtRef = useRef<number>(0)

  const isSupported =
    typeof window !== 'undefined' &&
    typeof navigator !== 'undefined' &&
    Boolean(navigator.mediaDevices?.getUserMedia) &&
    typeof MediaRecorder !== 'undefined' &&
    Boolean(pickRecorderMime())

  const cleanupStream = useCallback((clearChunks = true) => {
    if (timerRef.current) {
      clearInterval(timerRef.current)
      timerRef.current = null
    }
    streamRef.current?.getTracks().forEach((t) => t.stop())
    streamRef.current = null
    mediaRecorderRef.current = null
    if (clearChunks) {
      chunksRef.current = []
    }
  }, [])

  const revokePreview = useCallback(() => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl)
    }
  }, [previewUrl])

  const discardPreview = useCallback(() => {
    revokePreview()
    setPreviewUrl(null)
    setRecordedFile(null)
    setPhase('idle')
    setSeconds(0)
    setRecordedDurationSeconds(0)
    setError(null)
  }, [revokePreview])

  useEffect(() => {
    return () => {
      cleanupStream()
      revokePreview()
    }
  }, [cleanupStream, revokePreview])

  const stopInternal = useCallback(() => {
    const recorder = mediaRecorderRef.current
    if (!recorder || recorder.state === 'inactive') return
    try {
      if (recorder.state === 'recording') {
        try {
          recorder.requestData()
        } catch {
          /* Safari / browsers sem requestData */
        }
      }
      recorder.stop()
    } catch {
      cleanupStream()
      setPhase('idle')
      setSeconds(0)
    }
  }, [cleanupStream])

  const start = useCallback(async () => {
    if (!isSupported || phase === 'recording') return
    setError(null)
    discardPreview()

    const mime = pickRecorderMime()
    if (!mime) {
      setError('Gravação de áudio não suportada neste navegador.')
      return
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      streamRef.current = stream
      const recorder = new MediaRecorder(stream, { mimeType: mime })
      mediaRecorderRef.current = recorder
      chunksRef.current = []

      recorder.ondataavailable = (ev) => {
        if (ev.data && ev.data.size > 0) {
          chunksRef.current.push(ev.data)
        }
      }

      recorder.onerror = () => {
        setError('Erro ao gravar áudio.')
        cleanupStream()
        setPhase('idle')
        setSeconds(0)
      }

      recorder.onstop = () => {
        if (timerRef.current) {
          clearInterval(timerRef.current)
          timerRef.current = null
        }

        const elapsedSec = Math.max(
          1,
          Math.round((Date.now() - startedAtRef.current) / 1000)
        )

        const chunks = chunksRef.current.slice()
        const blob = new Blob(chunks, {
          type: mime.split(';')[0] || 'audio/webm',
        })
        cleanupStream()
        if (!blob.size) {
          setError(
            'Nenhum áudio foi capturado. Grave por pelo menos 1 segundo.'
          )
          setPhase('idle')
          setSeconds(0)
          setRecordedDurationSeconds(0)
          return
        }

        const baseMime = blob.type || 'audio/webm'
        setSeconds(elapsedSec)
        setRecordedDurationSeconds(elapsedSec)
        setPhase('processing')

        void (async () => {
          try {
            const file = await convertChatAudioForSend(blob, {
              filename: buildRecordedAudioFilename(baseMime),
              mimeType: baseMime,
            })
            const url = URL.createObjectURL(file)
            setRecordedFile(file)
            setPreviewUrl(url)
            setPhase('preview')
          } catch (err) {
            console.error('[chat-audio] falha ao preparar áudio', err)
            setError(
              'Não foi possível preparar o áudio para envio. Tente gravar novamente.'
            )
            setPhase('idle')
            setSeconds(0)
            setRecordedDurationSeconds(0)
          }
        })()
      }

      recorder.start(250)
      startedAtRef.current = Date.now()
      setSeconds(0)
      setPhase('recording')
      timerRef.current = setInterval(() => {
        const elapsed = Math.floor((Date.now() - startedAtRef.current) / 1000)
        setSeconds(elapsed)
        if (elapsed >= maxSeconds) {
          stopInternal()
        }
      }, 250)
    } catch (e) {
      cleanupStream()
      const denied =
        e instanceof DOMException &&
        (e.name === 'NotAllowedError' || e.name === 'PermissionDeniedError')
      setError(
        denied
          ? 'Permita o uso do microfone para gravar áudio.'
          : 'Não foi possível acessar o microfone.'
      )
      setPhase('idle')
      setSeconds(0)
    }
  }, [
    cleanupStream,
    discardPreview,
    isSupported,
    maxSeconds,
    phase,
    stopInternal,
  ])

  const stop = useCallback(() => {
    if (phase !== 'recording') return
    stopInternal()
  }, [phase, stopInternal])

  const toggleRecording = useCallback(async () => {
    if (phase === 'recording') {
      stop()
      return
    }
    if (phase === 'idle') {
      await start()
    }
  }, [phase, start, stop])

  const consumeRecordedFile = useCallback((): File | null => {
    const file = recordedFile
    discardPreview()
    return file
  }, [discardPreview, recordedFile])

  return {
    isSupported,
    phase,
    seconds,
    recordedDurationSeconds,
    previewUrl,
    recordedFile,
    error,
    start,
    stop,
    toggleRecording,
    discardPreview,
    consumeRecordedFile,
  }
}
